// Cache Management Utility
class CacheManager {
  constructor() {
    this.version = '2025.1.1758215694789';
    this.storageKey = 'app_version';
  }

  // Check if app version has changed
  hasVersionChanged() {
    const storedVersion = localStorage.getItem(this.storageKey);
    return storedVersion !== this.version;
  }

  // Update stored version
  updateStoredVersion() {
    localStorage.setItem(this.storageKey, this.version);
  }

  // Clear all caches
  async clearAllCaches() {
    try {
      // Clear service worker cache
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        await this.clearServiceWorkerCache();
      }

      // Clear browser cache
      await this.clearBrowserCache();

      // Clear localStorage (optional - be careful with this)
      // localStorage.clear();

      console.log('✅ All caches cleared successfully');
      return true;
    } catch (error) {
      console.error('❌ Error clearing caches:', error);
      return false;
    }
  }

  // Clear service worker cache
  async clearServiceWorkerCache() {
    return new Promise((resolve, reject) => {
      if (!navigator.serviceWorker.controller) {
        resolve();
        return;
      }

      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = (event) => {
        if (event.data.success) {
          resolve();
        } else {
          reject(new Error(event.data.error));
        }
      };

      navigator.serviceWorker.controller.postMessage(
        { type: 'CLEAR_CACHE' },
        [messageChannel.port2]
      );
    });
  }

  // Clear browser cache
  async clearBrowserCache() {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
    }
  }

  // Force reload with cache busting
  forceReload() {
    // Add timestamp to force reload
    const timestamp = new Date().getTime();
    const url = new URL(window.location);
    url.searchParams.set('_t', timestamp);
    window.location.href = url.toString();
  }

  // Add cache busting to URLs
  addCacheBusting(url) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}v=${this.version}&_t=${Date.now()}`;
  }

  // Check for updates and handle them
  async checkForUpdates() {
    if (this.hasVersionChanged()) {
      console.log('🔄 New version detected, clearing caches...');
      
      const cleared = await this.clearAllCaches();
      if (cleared) {
        this.updateStoredVersion();
        
        // Show update notification
        this.showUpdateNotification();
      }
    }
  }

  // Show update notification
  showUpdateNotification() {
    // Create a custom notification
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #667eea, #764ba2);
      color: white;
      padding: 16px 24px;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      max-width: 300px;
      animation: slideIn 0.3s ease-out;
    `;

    notification.innerHTML = `
      <div style="display: flex; align-items: center; gap: 12px;">
        <div style="font-size: 20px;">🔄</div>
        <div>
          <div style="font-weight: 600; margin-bottom: 4px;">App Updated!</div>
          <div style="font-size: 12px; opacity: 0.9;">New features and improvements available.</div>
        </div>
        <button onclick="this.parentElement.parentElement.remove()" style="
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          cursor: pointer;
          font-size: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
        ">×</button>
      </div>
    `;

    // Add CSS animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);

    document.body.appendChild(notification);

    // Auto remove after 5 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove();
      }
    }, 5000);
  }

  // Initialize cache management
  async init() {
    // Check for updates on app start
    await this.checkForUpdates();

    // Set up periodic update checks (every 5 minutes)
    setInterval(() => {
      this.checkForUpdates();
    }, 5 * 60 * 1000);
  }
}

// Export singleton instance
export default new CacheManager();